import { GlobalStyles as MuiGlobalStyles } from '@mui/material';

/**
 * Injects global CSS variables that power the gradient-mesh backdrop,
 * glass surfaces, gradient-text and the theme-dependent custom tokens.
 *
 * MUI's `palette` stays authoritative for component styles — these variables
 * are an extra layer for custom elements (Login backdrop, hero cards, etc.).
 */
export default function GlobalStyles() {
  return (
    <MuiGlobalStyles
      styles={(theme) => {
        const isDark = theme.palette.mode === 'dark';

        const tokens = isDark
          ? {
              '--bg': '#0a0b14',
              '--surface': 'rgba(18,20,31,0.7)',
              '--surface-solid': '#12141f',
              '--elevated': '#141623',
              '--subtle': '#171a27',
              '--text': '#eceefc',
              '--text-muted': '#a2a8c0',
              '--text-subtle': '#6b718f',
              '--border': 'rgba(255,255,255,0.08)',
              '--border-strong': 'rgba(255,255,255,0.16)',
              '--primary': '#8b8bff',
              '--primary-2': '#5ee7f1',
              '--primary-contrast': '#0a0b14',
              '--accent-pink': '#f472b6',
              '--success': '#34d399',
              '--warning': '#fbbf24',
              '--error': '#f87171',
              '--mesh-blob1': 'rgba(139, 139, 255, 0.22)',
              '--mesh-blob2': 'rgba(94, 231, 241, 0.16)',
              '--mesh-blob3': 'rgba(244, 114, 182, 0.12)',
              '--grid': 'rgba(255,255,255,0.035)',
              '--ring-bg': 'rgba(255,255,255,0.08)',
            }
          : {
              '--bg': '#f6f7fb',
              '--surface': 'rgba(255,255,255,0.72)',
              '--surface-solid': '#ffffff',
              '--elevated': '#ffffff',
              '--subtle': '#eef0f6',
              '--text': '#0b1020',
              '--text-muted': '#55607a',
              '--text-subtle': '#8591a8',
              '--border': 'rgba(15,23,42,0.08)',
              '--border-strong': 'rgba(15,23,42,0.16)',
              '--primary': '#5b5bf6',
              '--primary-2': '#22c3d1',
              '--primary-contrast': '#ffffff',
              '--accent-pink': '#ec4899',
              '--success': '#10b981',
              '--warning': '#f59e0b',
              '--error': '#ef4444',
              '--mesh-blob1': 'rgba(91, 91, 246, 0.22)',
              '--mesh-blob2': 'rgba(34, 195, 209, 0.18)',
              '--mesh-blob3': 'rgba(236, 72, 153, 0.14)',
              '--grid': 'rgba(15,23,42,0.04)',
              '--ring-bg': 'rgba(15,23,42,0.06)',
            };

        const primaryGrad = isDark
          ? 'linear-gradient(135deg, #8b8bff 0%, #5ee7f1 100%)'
          : 'linear-gradient(135deg, #5b5bf6 0%, #22c3d1 100%)';

        return {
          ':root': tokens,
          html: {
            colorScheme: theme.palette.mode,
          },
          '#root': {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            isolation: 'isolate',
          },

          /* ---------- Utility classes ---------- */
          '.gradient-text': {
            backgroundImage: primaryGrad,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          },
          '.gradient-surface': {
            background: primaryGrad,
            color: 'var(--primary-contrast)',
          },
          '.glass': {
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            backdropFilter: 'blur(16px) saturate(140%)',
            WebkitBackdropFilter: 'blur(16px) saturate(140%)',
          },

          /* ---------- Custom animated background mesh ---------- */
          '.app-bg': {
            position: 'fixed',
            inset: 0,
            zIndex: -1,
            pointerEvents: 'none',
            overflow: 'hidden',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
            },
            '&::before': {
              backgroundImage:
                'linear-gradient(var(--grid) 1px, transparent 1px),' +
                'linear-gradient(90deg, var(--grid) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 40%, #000 40%, transparent 100%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 40%, #000 40%, transparent 100%)',
              opacity: 0.6,
            },
            '&::after': {
              background:
                'radial-gradient(600px 300px at 10% -10%, var(--mesh-blob1), transparent 60%),' +
                'radial-gradient(500px 280px at 95% 8%, var(--mesh-blob2), transparent 60%),' +
                'radial-gradient(500px 280px at 50% 110%, var(--mesh-blob3), transparent 60%)',
              filter: 'blur(10px)',
              opacity: 0.9,
            },
          },

          /* ---------- Scrollbar styling ---------- */
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark
              ? 'rgba(255,255,255,0.15) transparent'
              : 'rgba(15,23,42,0.15) transparent',
          },
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-thumb': {
            background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.18)',
            borderRadius: 999,
          },
          '*::-webkit-scrollbar-track': { background: 'transparent' },

          /* ---------- Reduced motion ---------- */
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              transitionDuration: '0.01ms !important',
              animationDuration: '0.01ms !important',
              animationIterationCount: 1,
            },
          },
        };
      }}
    />
  );
}
